'use client';
/**
 * @file     Footer.tsx
 * @readme   ./README.md
 * @purpose  Site footer: crawlable link columns, Browse BC, a brand card pairing the Relta mark with the official app store badges, the licensee and brokerage disclosure as a three-column grid, legal links, and CREA attribution with the MLS Reciprocity logo. CSS modules so it server-renders inside the Emotion-free AppFrame shell.
 */

import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import { navigationCategories, routes } from '@/core/constants';
import type { NavigationItem } from '@/core/constants/navigation';
import { FOOTER_CITIES } from '@/core/config/footerCities';
import { useNavigationCategories } from '@/hooks/useNavigationCategories';
import { useAppTheme } from '@/core/theme/useAppTheme';
import { chromeLayoutTokens, semanticRoleColors } from '@/design-system/tokens';
import { brandConfig } from '@/core/config/brand/brandConfig';
import { LICENSEE_LINE } from '@/core/config/brand/licenseeLine';
import LogoSvg from '../Navbar/LogoSvg';
import MlsReciprocityLogoSvg from './MlsReciprocityLogoSvg';
import { CookiePreferencesButton } from '../SiteNotice/CookiePreferencesButton';
import { FooterAppBadges } from './FooterAppBadges';
import type { FooterProps } from './Footer.types';
import styles from './Footer.module.css';

const CURRENT_YEAR = 2026;

const Footer = ({ year = CURRENT_YEAR }: FooterProps) => {
  const { t } = useTranslation();
  const { theme } = useAppTheme();
  const footerCategoryOrder = ['buy', 'rent', 'sell', 'tools', 'learn'];
  const footerLearnOverride: NavigationItem[] = [
    { labelKey: 'nav.items.academyBuyer', path: routes.academyBuyer },
    { labelKey: 'nav.items.academySeller', path: routes.academySeller },
    { labelKey: 'nav.items.academyLandlord', path: routes.academyLandlord },
    { labelKey: 'nav.items.blog', path: routes.blog },
  ];
  /**
   * Data families that belong in the footer but not the top nav. The nav is a
   * short list of primary destinations; these are browse surfaces, and adding
   * them to navigationCategories would put them in both places at once.
   */
  const footerExtras: Record<string, NavigationItem[]> = {
    buy: [
      { labelKey: 'nav.items.homesByCity', path: routes.homesByCity },
      { labelKey: 'nav.items.marketReport', path: routes.marketReport },
      { labelKey: 'nav.items.marketSignals', path: routes.priceDrops },
      { labelKey: 'nav.items.buildings', path: routes.buildings },
      { labelKey: 'nav.items.hotDeals', path: routes.hotDeals },
      { labelKey: 'nav.items.presales', path: routes.presales },
      { labelKey: 'nav.items.affordability', path: routes.affordability },
      { labelKey: 'nav.items.rentVsMortgage', path: routes.rentVsMortgage },
      { labelKey: 'nav.items.mortgagePreApproval', path: routes.mortgagePreApproval },
      { labelKey: 'nav.items.costOfBuying', path: routes.costOfBuying },
      { labelKey: 'nav.items.bcPropertyTaxes', path: routes.bcPropertyTaxes },
    ],
    // The landlord funnel left the Sell menu for Rent; the footer keeps its link.
    rent: [{ labelKey: 'nav.items.findTenants', path: routes.findTenantsLanding }],
    sell: [
      { labelKey: 'nav.items.homeWorth', path: routes.homeWorth },
      { labelKey: 'nav.items.recentlySold', path: routes.recentlySold },
      { labelKey: 'nav.items.bestTimeToSell', path: routes.bestTimeToSell },
      { labelKey: 'nav.items.depreciationReport', path: routes.depreciationReport },
    ],
    tools: [
      { labelKey: 'nav.items.propertyTransferTaxCalculator', path: routes.propertyTransferTaxCalculator },
      { labelKey: 'nav.items.gstNewHomeRebate', path: routes.gstNewHomeRebate },
      { labelKey: 'nav.items.closingCosts', path: routes.closingCosts },
      { labelKey: 'nav.items.bcHomeFlippingTax', path: routes.bcHomeFlippingTax },
      { labelKey: 'nav.items.speculationVacancyTax', path: routes.speculationVacancyTax },
    ],
  };
  const liveCategories = useNavigationCategories(navigationCategories);
  const orderedCategories = [...liveCategories]
    .filter((c) => footerCategoryOrder.includes(c.key))
    .sort(
      (left, right) =>
        footerCategoryOrder.indexOf(left.key) - footerCategoryOrder.indexOf(right.key),
    );

  const cssVars = {
    '--ft-bg': theme.semanticColors.backgroundPrimary,
    '--ft-divider': theme.semanticColors.borderSubtle,
    '--ft-text-primary': theme.semanticColors.textPrimary,
    '--ft-text-secondary': theme.semanticColors.textSecondary,
    '--ft-text-muted': theme.semanticColors.textMuted,
    '--ft-link': semanticRoleColors.link,
    '--ft-link-hover': semanticRoleColors.linkHover,
    '--ft-focus-ring': theme.semanticColors.interactiveFocusRing,
    '--ft-surface-card': theme.semanticColors.surfaceCard,
    '--ft-radius-sm': `${theme.layout.radius.sm}px`,
    '--ft-radius-lg': `${theme.layout.radius.lg}px`,
    '--ft-max-width': `${chromeLayoutTokens.maxWidth}px`,
    '--ft-gutter': chromeLayoutTokens.gutter.fluid,
    '--ft-heading-font': theme.typography.fontFamilyHeading,
  } as React.CSSProperties;

  return (
    <footer className={styles.footer} style={cssVars}>
      <div className={styles.inner}>
        <nav
          className={styles.linkCols}
          aria-label={t('footer.navLabel', { defaultValue: 'Site links' })}
        >
          {orderedCategories.map((category) => {
            const items =
              category.key === 'learn'
                ? footerLearnOverride
                : [
                    ...category.items,
                    ...(footerExtras[category.key] ?? []).filter((extra) => !category.items.some((item) => item.path === extra.path)),
                  ];
            return (
              <div key={category.key} className={styles.linkCol}>
                <p className={styles.colTitle}>
                  {t(category.labelKey, { defaultValue: category.key })}
                </p>
                {items.map((item) => (
                  <Link key={item.path} href={item.path} className={styles.link}>
                    {t(item.labelKey, { defaultValue: item.labelKey })}
                  </Link>
                ))}
              </div>
            );
          })}

          <div className={styles.linkCol}>
            <p className={styles.colTitle}>
              {t('footer.forAgents.title', { defaultValue: 'For agents' })}
            </p>
            <Link href={routes.reltaAgentApp} className={styles.link}>
              {t('footer.forAgents.agentApp', { defaultValue: 'Relta Agent app' })}
            </Link>
            <Link href={routes.meet} className={styles.link}>
              {t('footer.forAgents.bookCall', { defaultValue: 'Book a call' })}
            </Link>
          </div>

          <div className={styles.linkCol}>
            <p className={styles.colTitle}>
              {t('footer.support.title', { defaultValue: 'Support' })}
            </p>
            <a href={`tel:${brandConfig.phone}`} className={styles.link}>
              {brandConfig.primaryContactPhoneLabel}
            </a>
            <a href={`mailto:${brandConfig.primaryContactEmail}`} className={styles.link}>
              {brandConfig.primaryContactEmailLabel}
            </a>
            <Link href={routes.aboutRay} className={styles.link}>
              {t('nav.items.aboutRay', { defaultValue: 'About Ray' })}
            </Link>
            <Link href={routes.contact} className={styles.link}>
              {t('footer.support.contact', { defaultValue: 'Contact' })}
            </Link>
          </div>
        </nav>

        {/* Sitewide entry into the geo pyramid (ADR-024). Kept to the highest
            inventory cities on purpose: a full city index on every page is
            boilerplate. "All BC cities" hands off to the province hub, which
            owns the complete list. */}
        <div className={styles.browseRow}>
          <p className={styles.colTitle}>
            {t('footer.browse.title', { defaultValue: 'Browse BC' })}
          </p>
          <div className={styles.browseLinks}>
            {FOOTER_CITIES.map((city) => (
              <Link key={city.slug} href={`/properties/${city.slug}`} className={styles.browseLink}>
                {city.name}
                <span className={styles.browseTail}>
                  {t('footer.browse.tail', { defaultValue: ' homes for sale' })}
                </span>
              </Link>
            ))}
            <Link href={routes.britishColumbia} className={styles.browseAll}>
              {t('footer.browse.all', { defaultValue: 'All BC cities' })}
            </Link>
          </div>
        </div>

        {/* The mark and tagline on the left, the store badges on the right,
            raised off the footer surface onto a card. */}
        <div className={styles.brandCard}>
          <div className={styles.brandRow}>
            <LogoSvg height={52} />
            <div className={styles.brandText}>
              <p className={styles.brandTitle}>{t('footer.descriptionTitle')}</p>
              <p className={styles.brandBody}>{t('footer.description')}</p>
            </div>
          </div>

          <FooterAppBadges compact />
        </div>

        {/* The licensee disclosure reads as three separate statements, so it is
            laid out as three columns rather than one tall paragraph. */}
        <div className={styles.legalGrid}>
          <address className={styles.legalCell}>
            {t('footer.trust.credit', { defaultValue: LICENSEE_LINE })}
            <br />
            {brandConfig.brokerageAddress}
            <br />
            <a href={`tel:${brandConfig.phone}`}>{brandConfig.primaryContactPhoneLabel}</a>
            {' · '}
            <a href={`mailto:${brandConfig.primaryContactEmail}`}>
              {brandConfig.primaryContactEmailLabel}
            </a>
          </address>
          <p className={styles.legalCell}>
            {t('footer.trust.notABrokerage', {
              defaultValue:
                'Relta Real Estate is not a brokerage. Real estate services are provided by Ray Rasouli, licensed with Real Broker B.C. Ltd.',
            })}
          </p>
          <p className={styles.legalCell}>
            {t('footer.trust.dataCredit', {
              defaultValue:
                'MLS®, IDX, and VOW listing data is provided through Ray Rasouli, founder of Relta and a member of Greater Vancouver REALTORS®.',
            })}
          </p>
        </div>

        <hr className={styles.divider} />

        <div className={styles.bottomRow}>
          <p className={styles.fineprint}>
            {t('footer.legal.copyright', {
              defaultValue: `© ${year} Ray Rasouli, Relta Real Estate.`,
              year,
            })}
          </p>
          <div className={styles.legalLinks}>
            <Link href={routes.privacy} className={styles.legalLink}>
              {t('footer.legal.privacyLink', { defaultValue: 'Privacy Policy' })}
            </Link>
            <CookiePreferencesButton className={`${styles.legalLink} ${styles.legalButton}`}>
              {t('siteNotice.footerLink')}
            </CookiePreferencesButton>
            <Link href={routes.terms} className={styles.legalLink}>
              {t('footer.legal.termsLink', { defaultValue: 'Terms of Service' })}
            </Link>
            <Link href={routes.disclosure} className={styles.legalLink}>
              {t('footer.legal.disclosureLink', { defaultValue: 'Referral Disclosure' })}
            </Link>
            <Link href={routes.reltaAgentPrivacy} className={styles.legalLink}>
              {t('footer.legal.reltaAgentPrivacyLink', { defaultValue: 'Relta Agent app: Privacy' })}
            </Link>
            <Link href={routes.reltaAgentTerms} className={styles.legalLink}>
              {t('footer.legal.reltaAgentTermsLink', { defaultValue: 'Relta Agent app: Terms' })}
            </Link>
          </div>
        </div>

        {/* CREA trademark notice + MLS Reciprocity logo, paired together
            since both are §8 attribution chrome. */}
        <div className={styles.creaRow}>
          <p className={styles.creaNotice}>
            {t('common.creaTrademarkStatement', {
              defaultValue:
                'The trademarks REALTOR®, REALTORS®, and the REALTOR® logo are controlled by The Canadian Real Estate Association (CREA) and identify real estate professionals who are members of CREA. MLS®, Multiple Listing Service®, and the associated logos are owned by The Canadian Real Estate Association (CREA) and identify the quality of services provided by real estate professionals who are members of CREA.',
            })}
          </p>
          <a
            href="https://www.realtor.ca/"
            target="_blank"
            rel="external nofollow noopener"
            aria-label="Visit Realtor.ca: Canadian MLS® Reciprocity"
            className={styles.reciprocityLink}
          >
            <MlsReciprocityLogoSvg height={56} />
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
